/* CE - NAD3 Offer Buy Box */
(() => {
  const ROOT_SELECTOR = '[data-ce-nad3-offer-buy-box]';
  const CTA_SELECTOR = '[data-ce-nad3-offer-cta]';

  function parseDiscountCodes(raw) {
    return (raw || '')
      .split(/[\n,]+/g)
      .map((code) => code.trim())
      .filter(Boolean);
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    const contentType = response.headers.get('content-type') || '';
    const data = contentType.includes('application/json')
      ? await response.json()
      : await response.text();

    if (!response.ok) {
      const message =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(message);
    }

    return data;
  }

  async function addSingleItem(item) {
    return postJSON('/cart/add.js', item);
  }

  async function addItemsBatch(items) {
    return postJSON('/cart/add.js', { items: items || [] });
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function setStatus(root, message) {
    const status = root.querySelector('[data-ce-nad3-offer-status]');
    if (!status) return;
    status.textContent = message || '';
  }

  function setBusy(button, isBusy) {
    if (!button) return;
    button.disabled = !!isBusy;
    button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    button.classList.toggle('is-busy', !!isBusy);
  }

  function cleanBogoSearch(raw) {
    let text = (raw || '').trim();
    if (!text) return '';

    try {
      if (/^https?:\/\//i.test(text)) {
        text = new URL(text).search || '';
      }
    } catch (error) {}

    const questionIndex = text.indexOf('?');
    if (questionIndex >= 0) {
      text = text.slice(questionIndex + 1);
    }

    text = text.replace(/^[?&]+/g, '').trim();

    if (!text || text.indexOf('=') === -1) return '';
    return text;
  }

  function applyBogoParams(root) {
    if (!root) return;

    const enabled =
      root.getAttribute('data-ce-nad3-offer-buy-box-bogo-enabled') === '1';

    if (!enabled) return;

    const raw =
      root.getAttribute('data-ce-nad3-offer-buy-box-bogo-params') || '';
    const clean = cleanBogoSearch(raw);

    if (!clean) return;

    let currentUrl;

    try {
      currentUrl = new URL(window.location.href);
    } catch (error) {
      return;
    }

    const params = new URLSearchParams(clean);
    let changed = false;

    params.forEach((value, key) => {
      if (!key) return;

      if (currentUrl.searchParams.get(key) !== value) {
        currentUrl.searchParams.set(key, value);
        changed = true;
      }
    });

    if (
      changed &&
      window.history &&
      typeof window.history.replaceState === 'function'
    ) {
      window.history.replaceState(
        window.history.state,
        '',
        currentUrl.toString()
      );
    }
  }

  function buildCheckoutUrl(root, discountCodes) {
    applyBogoParams(root);

    const rawCheckoutUrl =
      root.getAttribute('data-ce-nad3-offer-buy-box-checkout-url') ||
      '/checkout';

    let checkoutUrl;

    try {
      checkoutUrl = new URL(rawCheckoutUrl, window.location.origin);
    } catch (error) {
      checkoutUrl = new URL('/checkout', window.location.origin);
    }

    const currentParams = new URLSearchParams(window.location.search);

    currentParams.forEach((value, key) => {
      if (!key || checkoutUrl.searchParams.has(key)) return;
      checkoutUrl.searchParams.set(key, value);
    });

    if (discountCodes.length) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return `${checkoutUrl.pathname}${checkoutUrl.search}${checkoutUrl.hash}`;
    }

    return checkoutUrl.toString();
  }

  /* ============================================================
     BOGOS URL / CHECKOUT SYNCHRONIZATION
     ============================================================ */

  const BOGO_RELOAD_MARKER = '_ce_nad3_bogo_reload';
  let bogoReloadStateLoaded = false;
  let bogoReloadStateCache = null;

  function getBogoParams(root, activeOnly = true) {
    const enabled =
      root.getAttribute('data-ce-nad3-offer-buy-box-bogo-enabled') === '1';

    if (activeOnly && !enabled) return new URLSearchParams();

    const raw =
      root.getAttribute('data-ce-nad3-offer-buy-box-bogo-params') || '';

    const clean = cleanBogoSearch(raw).split('#')[0];

    return new URLSearchParams(clean);
  }

  function getConfiguredBogoKeys(root) {
    const keys = new Set();

    getBogoParams(root, false).forEach((value, key) => {
      if (key && key !== BOGO_RELOAD_MARKER) {
        keys.add(key);
      }
    });

    return keys;
  }

  function getBogoReloadState() {
    if (bogoReloadStateLoaded) return bogoReloadStateCache;

    bogoReloadStateLoaded = true;

    const raw = new URL(window.location.href)
      .searchParams.get(BOGO_RELOAD_MARKER);

    if (!raw) return null;

    try {
      const state = JSON.parse(raw);

      if (
        state &&
        state.mode === 'checkout' &&
        Number.isInteger(state.rootIndex) &&
        Number.isFinite(state.at) &&
        Date.now() - state.at >= 0 &&
        Date.now() - state.at < 30000
      ) {
        bogoReloadStateCache = state;
      }
    } catch (error) {}

    return bogoReloadStateCache;
  }

  function stripBogoReloadMarker() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);

    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash
    );
  }

  function restoreBogoReloadScroll(state) {
    const x = Number(state.scrollX);
    const y = Number(state.scrollY);

    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const html = document.documentElement;
    const originalBehavior = html.style.scrollBehavior;

    html.style.scrollBehavior = 'auto';

    const restore = () => {
      window.scrollTo(
        Math.max(0, x),
        Math.max(0, y)
      );
    };

    restore();

    window.requestAnimationFrame(() => {
      restore();
      window.requestAnimationFrame(restore);
    });

    window.setTimeout(() => {
      restore();
      html.style.scrollBehavior = originalBehavior;
    }, 120);
  }

  function reloadForBogoCheckout(root) {
    const expected = getBogoParams(root);
    const current = new URLSearchParams(window.location.search);
    const configuredKeys = getConfiguredBogoKeys(root);

    let needsReload = false;

    configuredKeys.forEach((key) => {
      const value = expected.get(key);

      if (
        value === null
          ? current.has(key)
          : current.get(key) !== value
      ) {
        needsReload = true;
      }
    });

    if (!needsReload) return false;

    const url = new URL(window.location.href);

    configuredKeys.forEach((key) => {
      url.searchParams.delete(key);
    });

    expected.forEach((value, key) => {
      if (key && key !== BOGO_RELOAD_MARKER) {
        url.searchParams.set(key, value);
      }
    });

    url.searchParams.set(
      BOGO_RELOAD_MARKER,
      JSON.stringify({
        mode: 'checkout',
        rootIndex: Array.from(
          document.querySelectorAll(ROOT_SELECTOR)
        ).indexOf(root),
        scrollX: Math.round(
          window.scrollX || window.pageXOffset || 0
        ),
        scrollY: Math.round(
          window.scrollY || window.pageYOffset || 0
        ),
        at: Date.now(),
      })
    );

    root.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
      setBusy(cta, true);
    });

    window.location.replace(url.toString());

    return true;
  }

  function extractBogoCartAttributes(root) {
    const attributes = {};
    let hasAny = false;

    getBogoParams(root).forEach((value, key) => {
      if (!key || key === BOGO_RELOAD_MARKER) return;

      attributes[key] = value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status})`);
    }

    return response.json();
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      const interval = 200;
      const maxWait = 6000;
      let elapsed = 0;

      const check = async () => {
        try {
          const cart = await fetchCart();

          if (
            cart &&
            typeof cart.item_count === 'number' &&
            cart.item_count > knownItemCount
          ) {
            resolve(true);
            return;
          }
        } catch (error) {
          resolve(false);
          return;
        }

        elapsed += interval;

        if (elapsed >= maxWait) {
          resolve(false);
          return;
        }

        window.setTimeout(check, interval);
      };

      window.setTimeout(check, interval);
    });
  }

  async function handleCheckoutClick(button) {
    const root = button.closest(ROOT_SELECTOR);

    if (!root || root.__ceNad3OfferSubmitting) return;

    if (reloadForBogoCheckout(root)) return;

    root.__ceNad3OfferSubmitting = true;
    setStatus(root, '');

    root.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
      setBusy(cta, true);
    });

    try {
      applyBogoParams(root);

      const clearCart =
        button.getAttribute('data-ce-nad3-offer-clear-cart') === '1';

      const mainVariantId = Number(
        (
          button.getAttribute('data-ce-nad3-offer-main-variant') || ''
        ).trim()
      );

      const mainQuantity = Math.max(
        1,
        Number(button.getAttribute('data-ce-nad3-offer-main-qty') || 1) || 1
      );

      const sellingPlanRaw = (
        button.getAttribute('data-ce-nad3-offer-main-selling-plan') || ''
      ).trim();

      const sellingPlanId = Number(sellingPlanRaw || 0);

      if (!mainVariantId) {
        throw new Error(
          'Missing main variant ID. Set it in this section’s Checkout behavior settings.'
        );
      }

      let freebies = [];

      try {
        freebies = JSON.parse(
          button.getAttribute('data-ce-nad3-offer-freebies') || '[]'
        );
      } catch (error) {
        freebies = [];
      }

      const mainItem = {
        id: mainVariantId,
        quantity: mainQuantity,
      };

      if (sellingPlanRaw && sellingPlanId > 0) {
        mainItem.selling_plan = sellingPlanId;
      }

      const freebieItems = (freebies || [])
        .filter((item) => item && Number(item.id) > 0)
        .map((item) => ({
          id: Number(item.id),
          quantity: Math.max(1, Number(item.quantity) || 1),
        }));

      const discountCodes = parseDiscountCodes(
        button.getAttribute('data-ce-nad3-offer-discounts')
      );

    const bogoAttributes = extractBogoCartAttributes(root);

    if (clearCart) {
      await postJSON('/cart/clear.js', {});
      await wait(180);
    }

    let cartBeforeAdd = null;

    if (bogoAttributes) {
      try {
        cartBeforeAdd = await fetchCart();
      } catch (error) {}
    }

    await addSingleItem(mainItem);

    let addedQuantity = mainQuantity;

    if (freebieItems.length) {
      try {
        await addItemsBatch(freebieItems);

        addedQuantity += freebieItems.reduce(
          (total, item) => total + item.quantity,
          0
        );
      } catch (freebieError) {
        console.warn(
          'CE NAD3 Offer Buy Box freebies failed to add:',
          freebieError
        );
      }
    }

    let cartAfterAdd = null;

    if (bogoAttributes) {
      try {
        cartAfterAdd = await fetchCart();
      } catch (error) {}
    }

    let cartAfterAttributeUpdate = null;

    if (bogoAttributes) {
      cartAfterAttributeUpdate = await postJSON(
        '/cart/update.js',
        {
          attributes: bogoAttributes,
        }
      );
    }

    if (bogoAttributes) {
      const latestCart =
        cartAfterAttributeUpdate || cartAfterAdd;

      const knownItemCount =
        latestCart &&
        typeof latestCart.item_count === 'number'
          ? latestCart.item_count
          : null;

      const baselineItemCount =
        cartBeforeAdd &&
        typeof cartBeforeAdd.item_count === 'number'
          ? cartBeforeAdd.item_count
          : null;

      const giftAlreadyPresent =
        knownItemCount !== null &&
        baselineItemCount !== null &&
        knownItemCount > baselineItemCount + addedQuantity;

      if (
        !giftAlreadyPresent &&
        knownItemCount !== null
      ) {
        await waitForBogoGift(knownItemCount);
      }
    }

    const checkoutUrl = buildCheckoutUrl(
      root,
      discountCodes
    );

    window.location.href = checkoutUrl;
    } catch (error) {
      const message =
        error && error.message
          ? error.message
          : 'Unable to start checkout. Please try again.';

      setStatus(root, message);

      root.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
        setBusy(cta, false);
      });

      root.__ceNad3OfferSubmitting = false;
    }
  }

  function bindGallery(root) {
    const gallery = root.querySelector('[data-ce-nad3-offer-gallery]');
    if (!gallery || gallery.__ceNad3OfferGalleryBound) return;

    const slides = Array.from(
      gallery.querySelectorAll('[data-ce-nad3-offer-gallery-slide]')
    );

    const thumbs = Array.from(
      gallery.querySelectorAll('[data-ce-nad3-offer-thumb]')
    );

    const prev = gallery.querySelector(
      '[data-ce-nad3-offer-gallery-nav="prev"]'
    );

    const next = gallery.querySelector(
      '[data-ce-nad3-offer-gallery-nav="next"]'
    );

    const thumbViewport = gallery.querySelector(
      '[data-ce-nad3-offer-thumb-viewport]'
    );

    if (!slides.length) return;

    gallery.__ceNad3OfferGalleryBound = true;

    let index = Math.max(
      0,
      slides.findIndex(
        (slide) => slide.getAttribute('data-ce-nad3-offer-active') === '1'
      )
    );

    const pauseVideos = (slide) => {
      if (!slide) return;

      slide.querySelectorAll('video').forEach((video) => {
        try {
          video.pause();
        } catch (error) {}
      });
    };

    const playVideos = (slide) => {
      if (!slide) return;

      slide.querySelectorAll('video').forEach((video) => {
        try {
          video.setAttribute('playsinline', '');
          video.play().catch(() => {});
        } catch (error) {}
      });
    };

    const syncNav = () => {
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= slides.length - 1;
    };

    const setActive = (nextIndex) => {
      index = Math.min(Math.max(nextIndex, 0), slides.length - 1);

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;

        slide.setAttribute('aria-hidden', active ? 'false' : 'true');

        if (active) {
          slide.setAttribute('data-ce-nad3-offer-active', '1');
        } else {
          slide.removeAttribute('data-ce-nad3-offer-active');
          pauseVideos(slide);
        }
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;

        thumb.setAttribute('aria-selected', active ? 'true' : 'false');

        if (active) {
          thumb.setAttribute('data-ce-nad3-offer-active', '1');
        } else {
          thumb.removeAttribute('data-ce-nad3-offer-active');
        }
      });

      const activeThumb = thumbs[index];

      if (thumbViewport && activeThumb) {
        const viewportRect = thumbViewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();

        const targetLeft =
          thumbViewport.scrollLeft +
          (thumbRect.left - viewportRect.left) -
          viewportRect.width / 2 +
          thumbRect.width / 2;

        thumbViewport.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'smooth',
        });
      }

      window.requestAnimationFrame(() => {
        playVideos(slides[index]);
      });

      syncNav();
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const targetIndex = Number(
          thumb.getAttribute('data-ce-nad3-offer-gallery-index') || 0
        );

        setActive(targetIndex);
      });
    });

    if (prev) {
      prev.addEventListener('click', () => setActive(index - 1));
    }

    if (next) {
      next.addEventListener('click', () => setActive(index + 1));
    }

    setActive(index);
  }

  function bindAccordions(root) {
    const accordions = Array.from(
      root.querySelectorAll('.ce-nad3-offer-buy-box__accordion')
    );

    if (!accordions.length) return;

    accordions.forEach((accordion) => {
      if (accordion.__ceNad3OfferAccordionBound) return;
      accordion.__ceNad3OfferAccordionBound = true;

      const button = accordion.querySelector(
        '[data-ce-nad3-offer-accordion-button]'
      );

      const panel = accordion.querySelector(
        '[data-ce-nad3-offer-accordion-panel]'
      );

      if (!button || !panel) return;

      button.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');

        accordions.forEach((sibling) => {
          if (sibling === accordion) return;

          sibling.classList.remove('is-open');

          const siblingButton = sibling.querySelector(
            '[data-ce-nad3-offer-accordion-button]'
          );

          const siblingPanel = sibling.querySelector(
            '[data-ce-nad3-offer-accordion-panel]'
          );

          if (siblingButton) {
            siblingButton.setAttribute('aria-expanded', 'false');
          }

          if (siblingPanel) {
            siblingPanel.querySelectorAll('video').forEach((video) => {
              try {
                video.pause();
              } catch (error) {}
            });

            siblingPanel.hidden = true;
          }
        });

        if (isOpen) {
          accordion.classList.remove('is-open');
          button.setAttribute('aria-expanded', 'false');

          panel.querySelectorAll('video').forEach((video) => {
            try {
              video.pause();
            } catch (error) {}
          });

          panel.hidden = true;
        } else {
          accordion.classList.add('is-open');
          button.setAttribute('aria-expanded', 'true');
          panel.hidden = false;

          const video = panel.querySelector('video');

          if (video) {
            video.play().catch(() => {});
          }
        }
      });
    });
  }

  function bindReviewActions(root) {
    const scrollButton = root.querySelector('[data-ce-nad3-offer-review-scroll]');

    if (scrollButton && !scrollButton.__ceNad3OfferReviewScrollBound) {
      scrollButton.__ceNad3OfferReviewScrollBound = true;

      scrollButton.addEventListener('click', () => {
        const rawTarget =
          scrollButton.getAttribute('data-ce-nad3-offer-review-target') || '';

        const cleanTarget = rawTarget.trim().replace(/^#/, '');

        if (!cleanTarget) return;

        const target = document.getElementById(cleanTarget);

        if (!target) return;

        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    const modal = root.querySelector('[data-ce-nad3-offer-reviews-modal]');
    const openButton = root.querySelector('[data-ce-nad3-offer-reviews-open]');

    if (!modal || !openButton || modal.__ceNad3OfferReviewsBound) return;

    modal.__ceNad3OfferReviewsBound = true;

    const closeButtons = Array.from(
      modal.querySelectorAll('[data-ce-nad3-offer-reviews-close]')
    );

    const track = modal.querySelector('[data-ce-nad3-offer-reviews-track]');
    const cards = Array.from(
      modal.querySelectorAll('[data-ce-nad3-offer-review-card]')
    );
    const dots = Array.from(
      modal.querySelectorAll('[data-ce-nad3-offer-review-dot]')
    );
    const prev = modal.querySelector('[data-ce-nad3-offer-reviews-prev]');
    const next = modal.querySelector('[data-ce-nad3-offer-reviews-next]');
    const closeButton = modal.querySelector('.ce-nad3-offer-buy-box__reviews-close');

    let index = 0;
    let previousActiveElement = null;
    let previousBodyOverflow = '';

    const setIndex = (nextIndex) => {
      if (!cards.length || !track) return;

      index = Math.min(Math.max(nextIndex, 0), cards.length - 1);
      track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

      cards.forEach((card, cardIndex) => {
        card.setAttribute('aria-hidden', cardIndex === index ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);

        if (active) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= cards.length - 1;
    };

    const openModal = () => {
      previousActiveElement = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      setIndex(0);

      window.requestAnimationFrame(() => {
        modal.classList.add('is-open');
        if (closeButton) closeButton.focus();
      });
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.hidden = true;
      document.body.style.overflow = previousBodyOverflow;

      if (
        previousActiveElement &&
        typeof previousActiveElement.focus === 'function'
      ) {
        previousActiveElement.focus();
      }
    };

    openButton.addEventListener('click', openModal);

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    if (prev) {
      prev.addEventListener('click', () => setIndex(index - 1));
    }

    if (next) {
      next.addEventListener('click', () => setIndex(index + 1));
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const targetIndex = Number(
          dot.getAttribute('data-ce-nad3-offer-review-dot') || 0
        );

        setIndex(targetIndex);
      });
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }

      if (event.key === 'ArrowLeft' && prev) {
        event.preventDefault();
        setIndex(index - 1);
      }

      if (event.key === 'ArrowRight' && next) {
        event.preventDefault();
        setIndex(index + 1);
      }
    });

    setIndex(0);
  }

  function getOrdinalSuffix(day) {
    const remainder100 = day % 100;

    if (remainder100 >= 11 && remainder100 <= 13) {
      return 'TH';
    }

    switch (day % 10) {
      case 1:
        return 'ST';
      case 2:
        return 'ND';
      case 3:
        return 'RD';
      default:
        return 'TH';
    }
  }

  function formatBatchDate(date) {
    const month = date
      .toLocaleDateString('en-US', {
        month: 'long',
      })
      .toUpperCase();

    const day = date.getDate();

    return `${month} ${day}${getOrdinalSuffix(day)}`;
  }

  function bindBatchStatusDates(root) {
    if (!root) return;

    const dateNodes = root.querySelectorAll(
      '[data-ce-nad3-offer-batch-date]'
    );

    if (!dateNodes.length) return;

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const dates = {
      previous_month_end: new Date(year, month, 0),
      today: new Date(year, month, today.getDate()),
      next_month_start: new Date(year, month + 1, 1),
    };

    dateNodes.forEach((node) => {
      const mode =
        node.getAttribute('data-ce-nad3-offer-batch-date') || '';

      const date = dates[mode];

      if (!date) return;

      node.textContent = formatBatchDate(date);
    });
  }

  function bindRoot(root) {
    if (!root || root.__ceNad3OfferBound) return;

    const reloadState = getBogoReloadState();

    const rootIndex = Array.from(
      document.querySelectorAll(ROOT_SELECTOR)
    ).indexOf(root);

    const expectedBogo = getBogoParams(root);
    const currentBogo = new URLSearchParams(
      window.location.search
    );

    let bogoMatches = true;

    expectedBogo.forEach((value, key) => {
      if (currentBogo.get(key) !== value) {
        bogoMatches = false;
      }
    });

    const shouldResume =
      reloadState &&
      reloadState.rootIndex === rootIndex &&
      bogoMatches;

    stripBogoReloadMarker();

    root.__ceNad3OfferBound = true;

    bindGallery(root);
    bindAccordions(root);
    bindReviewActions(root);
    bindBatchStatusDates(root);

    root.querySelectorAll(CTA_SELECTOR).forEach((button) => {
      button.addEventListener('click', () => handleCheckoutClick(button));
    });

    if (shouldResume) {
      bogoReloadStateCache = null;

      const button = root.querySelector(CTA_SELECTOR);

      if (button) {
        restoreBogoReloadScroll(reloadState);

        root.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
          setBusy(cta, true);
        });

        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            if (!root.__ceNad3OfferSubmitting) {
              handleCheckoutClick(button);
            }
          }, 30);
        });
      }
    }
  }

  function init(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      bindRoot(scope);
    }

    scope.querySelectorAll(ROOT_SELECTOR).forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {
      once: true,
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    init(document);
  });
})();
